import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, Users, TrendingUp, Building2, CreditCard, 
  ArrowLeftRight, PieChart, Upload, LogOut, Menu, X, Wallet, 
  Landmark, BarChart3, Scale, Briefcase, Settings, Shield, UserCog, Key, Eye, EyeOff, Lock,
  ChevronDown, LineChart, Building, MapPin, Zap, Receipt, Gift, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../App';
import { Button } from './ui/button';
import Logo from './Logo';
import Breadcrumb from './Breadcrumb';
import QuickActions from './QuickActions';
import FamilySwitcher from './FamilySwitcher';

// Admin emails that can access admin dashboard
const ADMIN_EMAILS = ['pundari@outlook.com', 'demo-premium@maadabbu.com', 'admin@maadabbu.com'];

// Inline Change Password Form Component
const ChangePasswordForm = ({ onComplete, onCancel }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('🎉 Password changed successfully!');
      onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
        <div className="relative">
          <input
            type={showCurrent ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="w-full p-3 pr-10 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            required
          />
          <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
            {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
        <div className="relative">
          <input
            type={showNew ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full p-3 pr-10 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            required
          />
          <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
            {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
        <input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
            confirmPassword && confirmPassword !== newPassword ? 'border-red-300' : ''
          }`}
          required
        />
        {confirmPassword && confirmPassword !== newPassword && (
          <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
        )}
      </div>
      <div className="flex gap-3 pt-2">
        <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500">
          {loading ? 'Saving...' : <><Lock size={16} className="mr-2" /> Save</>}
        </Button>
      </div>
    </form>
  );
};

const Layout = ({ children, user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [showChangePassword, setShowChangePassword] = React.useState(false);
  const [expandedWorkspace, setExpandedWorkspace] = React.useState(() => {
    // Auto-expand based on current path
    if (['/companies', '/performance', '/debt-management', '/transactions', '/bajaj-update', '/loan-payments', '/bulk-dividends', '/pledge-flow'].includes(location.pathname)) {
      return 'equity';
    }
    if (['/properties', '/rentals', '/projects'].includes(location.pathname) || location.pathname.startsWith('/projects/')) {
      return 'realestate';
    }
    if (['/accounts', '/imports', '/member-access'].includes(location.pathname) || location.pathname.startsWith('/imports/')) {
      return 'settings';
    }
    return 'equity'; // Default
  });

  // Keep the expanded workspace in sync as the user navigates between
  // sections (so e.g. clicking Properties → Projects keeps Real Estate open).
  React.useEffect(() => {
    const path = location.pathname;
    const equityPaths = ['/companies', '/performance', '/debt-management', '/transactions', '/bajaj-update', '/loan-payments', '/bulk-dividends', '/pledge-flow'];
    const realEstatePaths = ['/properties', '/rentals', '/projects'];
    const settingsPaths = ['/accounts', '/imports', '/member-access'];
    if (equityPaths.includes(path)) setExpandedWorkspace('equity');
    else if (realEstatePaths.includes(path) || path.startsWith('/projects/')) setExpandedWorkspace('realestate');
    else if (settingsPaths.includes(path) || path.startsWith('/imports/')) setExpandedWorkspace('settings');
    // Dashboard / login etc. → leave the previously-expanded workspace alone.
  }, [location.pathname]);

  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email);

  // Workspace definitions
  const workspaces = {
    equity: {
      label: 'Stock / Equity',
      icon: LineChart,
      color: 'emerald',
      items: [
        { icon: Briefcase, label: 'Know your Stock', path: '/companies' },
        { icon: BarChart3, label: 'Performance', path: '/performance' },
        { icon: Scale, label: 'Debt Insights', path: '/debt-management' },
        { icon: ArrowLeftRight, label: 'Transactions', path: '/transactions' },
      ]
    },
    realestate: {
      label: 'Real Estate',
      icon: Building,
      color: 'blue',
      items: [
        { icon: MapPin, label: 'Properties', path: '/properties' },
        { icon: Layers, label: 'Projects', path: '/projects' },
        { icon: Building2, label: 'Rentals', path: '/rentals' },
      ]
    },
    settings: {
      label: 'Settings',
      icon: Settings,
      color: 'gray',
      items: [
        { icon: Landmark, label: 'Accounts', path: '/accounts' },
        { icon: Upload, label: 'Imports', path: '/imports' },
        { icon: UserCog, label: 'Member Access', path: '/member-access' },
      ]
    }
  };

  // General menu items (outside workspaces) - only Dashboard and Admin
  const generalMenuItems = [
    { icon: Home, label: 'Dashboard', path: '/dashboard' },
    // Admin menu item - only for admin users
    ...(isAdmin ? [{ icon: Shield, label: 'Admin', path: '/admin', isAdmin: true }] : []),
  ];

  // Check if path is in a workspace
  const isPathInWorkspace = (workspaceKey) => {
    return workspaces[workspaceKey].items.some(item => item.path === location.pathname);
  };

  // Toggle workspace expansion
  const toggleWorkspace = (key) => {
    setExpandedWorkspace(expandedWorkspace === key ? null : key);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    toast.success('Logged out successfully');
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex sidebar flex-col justify-between">
        <div>
          <div className="px-6 mb-4">
            <Logo size="default" />
          </div>
          
          {/* Family Switcher */}
          <div className="px-4 mb-6">
            <FamilySwitcher />
          </div>
          
          <nav className="space-y-1">
            {/* Dashboard - Always visible at top */}
            <div
              data-testid="nav-dashboard"
              className={`sidebar-item ${location.pathname === '/dashboard' ? 'active' : ''}`}
              onClick={() => navigate('/dashboard')}
            >
              <Home size={20} />
              <span>Dashboard</span>
            </div>

            {/* Workspace Sections */}
            {Object.entries(workspaces).map(([key, workspace]) => {
              const WorkspaceIcon = workspace.icon;
              const isExpanded = expandedWorkspace === key;
              const hasActivePath = isPathInWorkspace(key);
              
              return (
                <div key={key} className="mt-2">
                  {/* Workspace Header */}
                  <div
                    className={`flex items-center gap-2 px-4 py-2 cursor-pointer rounded-lg transition-all ${
                      hasActivePath 
                        ? `bg-${workspace.color}-50 text-${workspace.color}-700` 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    onClick={() => toggleWorkspace(key)}
                  >
                    <WorkspaceIcon size={18} className={hasActivePath ? `text-${workspace.color}-600` : ''} />
                    <span className="font-medium text-sm flex-1">{workspace.label}</span>
                    <ChevronDown 
                      size={16} 
                      className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                    />
                  </div>
                  
                  {/* Workspace Items */}
                  {isExpanded && (
                    <div className="ml-2 mt-1 space-y-0.5 border-l-2 border-gray-200 pl-2">
                      {workspace.items.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;
                        return (
                          <div
                            key={item.path}
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`sidebar-item py-2 ${isActive ? 'active' : ''}`}
                            onClick={() => navigate(item.path)}
                          >
                            <Icon size={18} />
                            <span className="text-sm">{item.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Divider */}
            <div className="border-t border-gray-200 my-3"></div>

            {/* General Menu Items */}
            {generalMenuItems.slice(1).map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <div
                  key={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`sidebar-item ${isActive ? 'active' : ''} ${item.isAdmin ? 'border-t border-gray-200 mt-2 pt-2' : ''}`}
                  onClick={() => navigate(item.path)}
                >
                  <Icon size={20} className={item.isAdmin ? 'text-amber-600' : ''} />
                  <span className={item.isAdmin ? 'font-semibold text-amber-700' : ''}>{item.label}</span>
                  {item.isAdmin && (
                    <span className="ml-auto px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">
                      Admin
                    </span>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
        <div className="px-6 pb-4">
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium">{user?.name}</p>
            <p className="text-xs text-gray-500">{user?.email}</p>
            <button
              onClick={() => setShowChangePassword(true)}
              className="mt-2 text-xs text-primary hover:text-primary/80 flex items-center gap-1"
            >
              <Key size={12} />
              Change Password
            </button>
          </div>
          <button
            data-testid="logout-button"
            className="btn-outline w-full flex items-center justify-center gap-2"
            onClick={handleLogout}
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50">
        <div className="flex items-center justify-between p-4">
          <Logo size="small" />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="bg-white border-t border-gray-200 shadow-lg max-h-[80vh] overflow-y-auto">
            <nav className="p-4 space-y-2">
              {/* Dashboard */}
              <div
                data-testid="mobile-nav-dashboard"
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer ${location.pathname === '/dashboard' ? 'bg-[#D1FAE5] text-primary' : 'hover:bg-gray-50'}`}
                onClick={() => {
                  navigate('/dashboard');
                  setMobileMenuOpen(false);
                }}
              >
                <Home size={20} />
                <span className="font-medium">Dashboard</span>
              </div>

              {/* Workspace Sections */}
              {Object.entries(workspaces).map(([key, workspace]) => {
                const WorkspaceIcon = workspace.icon;
                const isExpanded = expandedWorkspace === key;
                const hasActivePath = isPathInWorkspace(key);
                
                return (
                  <div key={key} className="mt-2">
                    {/* Workspace Header */}
                    <div
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer ${
                        hasActivePath ? 'bg-gray-100' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => toggleWorkspace(key)}
                    >
                      <WorkspaceIcon size={20} className={key === 'equity' ? 'text-emerald-600' : 'text-blue-600'} />
                      <span className="font-semibold flex-1">{workspace.label}</span>
                      <ChevronDown 
                        size={18} 
                        className={`transition-transform text-gray-400 ${isExpanded ? 'rotate-180' : ''}`} 
                      />
                    </div>
                    
                    {/* Workspace Items */}
                    {isExpanded && (
                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-3">
                        {workspace.items.map((item) => {
                          const Icon = item.icon;
                          const isActive = location.pathname === item.path;
                          return (
                            <div
                              key={item.path}
                              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                              className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer ${isActive ? 'bg-[#D1FAE5] text-primary' : 'hover:bg-gray-50'}`}
                              onClick={() => {
                                navigate(item.path);
                                setMobileMenuOpen(false);
                              }}
                            >
                              <Icon size={18} />
                              <span className="font-medium text-sm">{item.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Divider */}
              <div className="border-t border-gray-200 my-3"></div>

              {/* General Menu Items */}
              {generalMenuItems.slice(1).map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <div
                    key={item.path}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer ${isActive ? 'bg-[#D1FAE5] text-primary' : 'hover:bg-gray-50'}`}
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                  >
                    <Icon size={20} />
                    <span className="font-medium">{item.label}</span>
                  </div>
                );
              })}
              
              <button
                data-testid="mobile-logout-button"
                className="btn-outline w-full flex items-center justify-center gap-2 mt-4"
                onClick={handleLogout}
              >
                <LogOut size={18} />
                Logout
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 lg:mt-0 mt-16 overflow-x-auto">
        <div className="p-3 sm:p-4 md:p-6 lg:p-12 max-w-7xl mx-auto">
          <Breadcrumb />
          {children}
        </div>
      </main>

      {/* Global Quick Actions FAB */}
      <QuickActions />

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-6 text-white">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur">
                  <Key size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Change Password</h2>
                  <p className="text-emerald-100 text-sm">Keep your account secure</p>
                </div>
              </div>
            </div>
            <ChangePasswordForm onComplete={() => setShowChangePassword(false)} onCancel={() => setShowChangePassword(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;